import { useEffect, useRef, useState } from "react"
import axios from "axios"
import { LogIn } from "lucide-react"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api"

/**
 * FOR-194: shown over the living app when a request comes back 401
 * unauthenticated. The page underneath — editor state included — is never
 * torn down; auth happens in an OAuth popup (Google, so there is no password
 * to re-enter) and a 2s poll notices the new session and dismisses.
 *
 * AlertDialog rather than Dialog: no close button, no escape/overlay dismiss.
 * There is nothing sensible to dismiss to — the app behind is deauthenticated.
 */
export function SessionExpiredDialog({
  open,
  onSignedIn,
}: {
  open: boolean
  onSignedIn: () => Promise<void>
}) {
  const [waiting, setWaiting] = useState(false)
  const popupRef = useRef<Window | null>(null)

  useEffect(() => {
    if (!open || !waiting) {
      return
    }

    const timer = window.setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/user`, { withCredentials: true })
        if (response.data.authenticated) {
          window.clearInterval(timer)
          popupRef.current?.close()
          setWaiting(false)
          await onSignedIn()
        }
      } catch {
        // Not signed in yet (or the API blipped) — keep polling.
      }
    }, 2000)

    return () => window.clearInterval(timer)
  }, [open, waiting, onSignedIn])

  const openLogin = () => {
    const baseUrl = import.meta.env.VITE_API_URL?.replace(/\/api$/, "") || "http://localhost:8000"
    popupRef.current = window.open(
      `${baseUrl}/api/auth/google`,
      "forge-login",
      "width=520,height=640"
    )
    // Popup blocked → fall back to a new tab; the poll still notices.
    if (!popupRef.current) {
      window.open(`${baseUrl}/api/auth/google`, "_blank")
    }
    setWaiting(true)
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Your session expired</AlertDialogTitle>
          <AlertDialogDescription>
            Your unsaved work is safe on this page — sign in again to continue
            exactly where you left off.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button onClick={openLogin} disabled={waiting}>
            <LogIn className="mr-2 h-4 w-4" />
            {waiting ? "Waiting for sign-in…" : "Sign in with Google"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
